import { MESSAGE_TEMPLATE_CREATE_METHOD_OPTIONS } from "@constants/message-template-create-method.options";
import { resources } from "@constants/hardcoded-strings";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { MessageTemplateSelectFormGroup } from "@containers/common/select-form-groups";
import { TemplateAvailabilityLevelSection } from "@containers/modals/add-message-template-modal/template-availability-level-section/template-availability-level-section";
import { ButtonTypes } from "@enums/button-types";
import { MessageTemplateCreateMethodTypes } from "@enums/message-template-create-method-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { SelectOption } from "@idw-types/common";
import { MessageTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/message-template/message-template-validation-errors";
import { FormattedMessageTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/message-template";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { DEFAULT_EMAIL_MESSAGE_TEMPLATE, DEFAULT_SMS_MESSAGE_TEMPLATE, MESSAGE_TEMPLATE_TYPE_OPTIONS } from "@constants/message-template-constants";
import { isNullOrUndefined } from "@utils/object-util";
import { MessageTemplateTypes } from "@enums/message-template-types";

type MessageTemplateMetadataFormProps = {
    session: IDWSession;
    labelWidth: LabelWidthTypes;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    validationErrors: MessageTemplateGeneralInfoValidationErrors;
    state: FormattedMessageTemplate;
    requestInProgress: boolean;
    messageTemplates: Array<FormattedMessageTemplate>;
    serviceOrganizationId: string;
    onModalClose: () => void;
    handleSubmit: (event: FormEvent) => void;
    setState: (state: FormattedMessageTemplate) => void;
};

export const MessageTemplateMetadataForm = (props: MessageTemplateMetadataFormProps) => {
    const {
        labelWidth,
        axnCompanies,
        selfServiceVerificationWhiteLabeledEmails,
        validationErrors,
        state,
        requestInProgress,
        session,
        serviceOrganizationId,
        messageTemplates,
        onModalClose,
        handleSubmit,
        setState
    } = props;

    type AddMessageTemplateState = {
        requestInProgress: boolean;
        selectedCreateMethod: MessageTemplateCreateMethodTypes;
        selectedMessageTemplateOption: SelectOption;
        selectedDeliveryMethodOption: SelectOption;
        selectedFileName: string;
        selectedFile: File;
        selectedVerificationServiceOption: SelectOption;
    };
    const [newState, setNewState] = useState<AddMessageTemplateState>({
        requestInProgress: false,
        selectedCreateMethod: MessageTemplateCreateMethodTypes.CREATE_FROM_SCRATCH || null,
        selectedMessageTemplateOption: null,
        selectedDeliveryMethodOption: isNullOrUndefined(state.type) ? null : { value: state.type, label: state.type },
        selectedFileName: "",
        selectedFile: null,
        selectedVerificationServiceOption: null,
    });
    const handleUpdateCreateMethod = (newValue: SelectOption<MessageTemplateCreateMethodTypes>) => {
        setNewState(newState => ({
            ...newState,
            selectedCreateMethod: newValue.value
        }));
    };

    const handleUpdateName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            name: event.target.value
        });
    };

    const handleUpdateDeliveryMethod = (newValue: SelectOption) => {
        if (newValue) {
            setNewState(newState => ({
                ...newState,
                selectedDeliveryMethodOption: newValue
            }))
            if (newValue.value == MessageTemplateTypes.EMAIL) {
                setState({
                    ...state,
                    type: newValue.value,
                    sender: resources.pages.messageTemplateDetails.tabs.variables.defaultEmailSender.title,
                    template: DEFAULT_EMAIL_MESSAGE_TEMPLATE

                })
            } else {
                setState({
                    ...state,
                    type: newValue.value,
                    sender: resources.pages.messageTemplateDetails.tabs.variables.defaultSMSSender.title,
                    template: DEFAULT_SMS_MESSAGE_TEMPLATE
                })
            }
        }
    }

    const handleUpdateMessageTemplate = (newValue: SelectOption) => {
        if (newValue) {
            setNewState(newState => ({
                ...newState,
                selectedMessageTemplateOption: newValue
            }));
            const selectedTemplate = messageTemplates.find(template => template.id === newValue.value);
            if (selectedTemplate) {
                setState({
                    ...state,
                    template: messageTemplates.find(template => template.id === newValue.value).template,
                })
            }
        }
    };
    const selectedCreateMethod: SelectOption<MessageTemplateCreateMethodTypes> = useMemo(() => {
        return MESSAGE_TEMPLATE_CREATE_METHOD_OPTIONS.find(option => option.value === newState.selectedCreateMethod);
    }, [MESSAGE_TEMPLATE_CREATE_METHOD_OPTIONS, newState.selectedCreateMethod]);

    const resource = resources.modals.addMessageTemplate;

    return (
        <Form onSubmit={handleSubmit}>
            <Modal.Header id={"add-message-template-title"} closeButton>
                <Modal.Title>{resource.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <SelectFormGroup
                    controlId={`message-template-create-method-select`}
                    label={resource.inputs.iWantTo.label}
                    ariaLabel={resource.inputs.iWantTo.label}
                    automationId={`message-template-create-method-select`}
                    onChange={handleUpdateCreateMethod}
                    options={MESSAGE_TEMPLATE_CREATE_METHOD_OPTIONS}
                    selectedOption={selectedCreateMethod}
                    disabled={requestInProgress}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                <hr />
                <SelectFormGroup
                    controlId="delivery-method-input"
                    label={resources.modals.verificationLinkProvider.inputs.deliveryMethod.label}
                    ariaLabel={resources.modals.verificationLinkProvider.inputs.deliveryMethod.label}
                    automationId="delivery-method-input"
                    selectedOption={newState.selectedDeliveryMethodOption}
                    onChange={handleUpdateDeliveryMethod}
                    disabled={requestInProgress}
                    isRequired={true}
                    options={MESSAGE_TEMPLATE_TYPE_OPTIONS}
                    isSearchable={true}
                    isClearable={false}
                    labelWidth={labelWidth}
                />

                {selectedCreateMethod?.value === MessageTemplateCreateMethodTypes.USE_TEMPLATE && (
                    <div>

                        {(newState.selectedDeliveryMethodOption != null &&
                            <MessageTemplateSelectFormGroup
                                idPrefix={"message-template-select"}
                                onChange={handleUpdateMessageTemplate}
                                messageTemplates={messageTemplates}
                                selectedOption={newState.selectedMessageTemplateOption}
                                disabled={requestInProgress}
                                selectedType={newState.selectedDeliveryMethodOption.value}
                                isRequired={true}
                                isSearchable={true}
                                isClearable={false}
                                labelWidth={labelWidth}
                            />


                        )}
                    </div>
                )}
                <TemplateAvailabilityLevelSection
                    session={session}
                    isDisabled={requestInProgress}
                    labelWidth={labelWidth}
                    axnCompanies={axnCompanies}
                    state={state}
                    serviceOrganizationId={serviceOrganizationId}
                    setState={setState}
                    validationErrors={validationErrors}
                    newTemplate={true}
                    selfServiceVerificationWhiteLabeledEmails={selfServiceVerificationWhiteLabeledEmails}
                />

                <hr />
                <InputFormGroup
                    controlId="message-template-name-input"
                    automationId="message-template-name-input"
                    label={resource.inputs.name.label}
                    ariaLabel={resource.inputs.name.label}
                    placeholder={resource.inputs.name.placeholder}
                    value={state.name}
                    onChange={handleUpdateName}
                    disabled={requestInProgress}
                    isRequired={true}
                    labelWidth={labelWidth}
                />
                {validationErrors.nameError}

                <hr />

            </Modal.Body>
            <Modal.Footer>
                <Button id="add-message-template-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                <ButtonWithSpinner
                    id="add-message-template-next-to-content-button"
                    disabled={requestInProgress || validationErrors.count > 0}
                    hideSpinner={validationErrors.count > 0}
                    variant="primary"
                    type={ButtonTypes.SUBMIT}
                    label={resource.buttons.nextToContent.label}
                />
            </Modal.Footer>
        </Form>
    );
};